import { featuresEnabled } from '@cigna/features';
import { conversationsQuery } from '@cigna/omni/conversations-state';
import {
  isAgentTimedOutConversation,
  isAsyncCHEConversation,
  isAsyncScheduleConversation,
  isAsyncSOConversation,
  isConversation,
  isLiveInitialMessage,
  isLiveScheduleConversation,
  isLiveSOConversation,
  isScheduleCoachingContextTags,
  outroMessages,
  isDmConversation,
  content,
  isClinicalNursingContextTags,
  ConversationDTOStatus,
} from '@cigna/omni/shared';
import {
  ConversationEvent,
  ConversationMessageEventDTO,
} from '@cigna/vampire-dto';
import { createSelector } from '@ngrx/store';
import { OutroObj } from '../interfaces';
import { getCachedTransformedEvents } from '../transforms/events.transform';

const selectInputEnabled = createSelector(
  conversationsQuery.selectConversation,
  conversationsQuery.selectConversationEvents,
  featuresEnabled(['enableInput']),
  (conversation, conversationEvents, enableInput = false) => {
    const filteredMessages = (conversationEvents || []).filter(
      (event: ConversationEvent): event is ConversationMessageEventDTO =>
        event.type === 'message',
    );

    if (enableInput) {
      return true;
    }

    const message = {
      ...(filteredMessages[filteredMessages.length - 1] || {}),
    };

    if (
      (conversation as ConversationDTOStatus)?.status === 'AGENT_JOINED' &&
      conversation?.transferIds?.length &&
      conversation.state !== 'closed'
    ) {
      return true;
    }

    if (message && message.dialog) {
      return message.dialog.acceptInput;
    }
    if (conversation && isLiveInitialMessage(conversation)) {
      return false;
    }
    if (conversation && conversation.type === 'bot') {
      return isScheduleCoachingContextTags(conversation.context.tags);
    }
    return conversation && conversation.state !== 'closed';
  },
);

const selectOutro = createSelector(
  conversationsQuery.selectConversation,
  conversationsQuery.selectConversationEvents,
  featuresEnabled(['isEvernorthEnabled']),
  featuresEnabled(['shouldEnableChat']),
  (
    conversation,
    conversationEvents,
    isEvernorthEnabled,
    chcpChatEnabled,
  ): OutroObj => ({
    message: isEvernorthEnabled
      ? outroMessages.evernorthMessage
      : chcpChatEnabled
      ? outroMessages.thankYouMessage
      : outroMessages.message,
    footer:
      conversation &&
      conversationEvents &&
      isAgentTimedOutConversation(conversationEvents)
        ? outroMessages.agentTimeoutFooter
        : outroMessages.footer,
    show: !!conversation && conversation.state === 'closed',
  }),
);

const selectEvents = createSelector(
  conversationsQuery.selectConversation,
  featuresEnabled(['personalGuideChat']),
  featuresEnabled(['isEvernorthEnabled']),
  (conversation, isPersonalGuide, isEvernorthEnabled) =>
    conversation && isConversation(conversation)
      ? getCachedTransformedEvents(
          conversation,
          isPersonalGuide,
          isEvernorthEnabled,
        )
      : [],
);

const selectEventsForBackToPrevious = createSelector(
  conversationsQuery.selectConversation,
  conversationsQuery.selectConversationEvents,
  featuresEnabled(['enableNewPreChatWelcome']),
  (conversation, selectConversationEvents, enableNewPreChatWelcome) => {
    if (
      conversation &&
      isConversation(conversation) &&
      enableNewPreChatWelcome
    ) {
      const checkMessageEventLength = selectConversationEvents.filter(
        (e) => e.type === 'message',
      ).length;
      if (
        checkMessageEventLength > 1 &&
        selectConversationEvents.every((event) => event.type !== 'transfer')
      ) {
        return true;
      }
    }
    return false;
  },
);

const replaceBackToPreviousWithEndChat = createSelector(
  conversationsQuery.getConversationMetadata,
  (convMeta) =>
    convMeta && (!convMeta.claimDetails || convMeta.claimDetails.medicare),
);

const selectConversationType = createSelector(
  conversationsQuery.selectConversation,
  (conversation) => {
    if (!conversation) {
      return 'noop';
    }

    if (isLiveSOConversation(conversation.context, conversation.type)) {
      return 'live';
    }

    if (isAsyncSOConversation(conversation.context, conversation.type)) {
      return 'asyncSupport';
    }

    if (isAsyncCHEConversation(conversation.context, conversation.type)) {
      return 'asyncChe';
    }

    if (isLiveScheduleConversation(conversation.context, conversation.type)) {
      return 'live';
    }

    if (isAsyncScheduleConversation(conversation.context, conversation.type)) {
      return 'asyncScheduleCoaching';
    }

    if (isDmConversation(conversation.context, conversation.type)) {
      return 'dm';
    }

    if (isClinicalNursingContextTags(conversation.context.tags)) {
      return 'hil';
    }

    return 'noop';
  },
);

const selectDisclaimer = createSelector(selectConversationType, (type) =>
  type === 'dm' ? content.dmDisclaimer : content.disclaimer,
);

export const messagingQuery = {
  selectConversationType,
  selectDisclaimer,
  selectEvents,
  selectInputEnabled,
  selectOutro,
  selectEventsForBackToPrevious,
  replaceBackToPreviousWithEndChat,
};
